/**
 * Per-sport primary "winner / moneyline" markets.
 *
 * The Sports API returns every operator market on every request — the SDK
 * filters them client-side after the fetch. Every code you pass through
 * `useServerOddsByDate` / `useServerBatchMatchOdds` is routed through the
 * data-layer's `getMarketTypeEnum`, which upper-cases unknown codes and
 * hard-maps `"1x2"` to football's `FT_1X2`. Sending `"1x2"` for a
 * basketball board therefore filters basketball's actual winner market
 * (`WINNER_OT`) out of the response.
 *
 * The map below is the empirically observed winner market per sport as
 * of 2026-09-10 — verified against `/v1/odds/fixtures` for each sport.
 * Consumers pick the primary market for the sport they're rendering.
 */
import type { LivescoreSport } from "@/views/livescore/utils/livescore";

/**
 * Winner market codes per sport, in the order the UI prefers to render them.
 *
 * Sports API operators aren't consistent — for the same fixture, one book
 * publishes `FT_1X2` (3-way regular-time result) while another only publishes
 * the OT-inclusive `WINNER_OT` / `WINNER_OT_PEN` (2-way). Passing only one
 * of them client-side drops every operator that offers the other, so we
 * pass every winner market we accept for that sport and let the response
 * order pick the primary. Verified against 5 days of `/v1/odds/fixtures`:
 * football almost always has FT_1X2 only, basketball/AF split roughly
 * 50/50 between FT_1X2 and WINNER_OT, hockey splits between FT_1X2 and
 * WINNER_OT_PEN, tennis only has WINNER.
 *
 * `"1x2"` is a lowercase alias the data-layer's `getMarketTypeEnum` maps to
 * `FT_1X2`. Unknown codes fall through upper-cased unchanged.
 */
export const SPORT_PRIMARY_ODDS_MARKETS: Record<LivescoreSport, string[]> = {
    football: ["1x2"],
    basketball: ["FT_1X2", "WINNER_OT"],
    "american-football": ["FT_1X2", "WINNER_OT"],
    hockey: ["FT_1X2", "WINNER_OT_PEN"],
    tennis: ["WINNER"],
};

/** Convenience accessor. Falls back to football's markets for an unknown sport. */
export function primaryMarketsForSport(sport: LivescoreSport): string[] {
    return SPORT_PRIMARY_ODDS_MARKETS[sport] ?? SPORT_PRIMARY_ODDS_MARKETS.football;
}

/**
 * Broader per-sport market list for the dedicated Odds tab of the multi-sport
 * event page. Includes the winner markets from `SPORT_PRIMARY_ODDS_MARKETS`
 * plus common secondary markets (totals, handicap, half/period results).
 *
 * Unknown codes fall through the SDK's `getMarketTypeEnum` upper-cased, so
 * anything not published by the operator is silently dropped from the
 * response — the UI just renders whatever comes back.
 */
export const SPORT_ODDS_MARKETS: Record<LivescoreSport, string[]> = {
    football: [
        "1x2",
        "DOUBLE_CHANCE",
        "DRAW_NO_BET",
        "BTTS",
        "HT_1X2",
        "HT_FT",
        "OVER_GOALS_0_5",
        "OVER_GOALS_1_5",
        "OVER_GOALS_2_5",
        "OVER_GOALS_3_5",
        "ASIAN_HANDICAP_neg1_5",
        "ASIAN_HANDICAP_neg0_5",
        "ASIAN_HANDICAP_0_5",
        "ASIAN_HANDICAP_1_5",
    ],
    basketball: [
        "FT_1X2",
        "WINNER_OT",
        "WINNER_INCL_OT",
        "HT_1X2",
        "HALF_TIME_1X2",
        "OVER_TOTAL",
        "UNDER_OVER",
        "OVER_UNDER",
        "HANDICAP",
        "ASIAN_HANDICAP",
        "TOTAL_POINTS",
        "OVER_POINTS",
        "QUARTER_1_1X2",
        "QUARTER_2_1X2",
        "QUARTER_3_1X2",
        "QUARTER_4_1X2",
    ],
    "american-football": [
        "FT_1X2",
        "WINNER_OT",
        "WINNER_INCL_OT",
        "OVER_TOTAL",
        "OVER_UNDER",
        "HANDICAP",
        "ASIAN_HANDICAP",
        "QUARTER_1_1X2",
        "QUARTER_2_1X2",
        "QUARTER_3_1X2",
        "QUARTER_4_1X2",
        "HALF_TIME_1X2",
    ],
    hockey: [
        "FT_1X2",
        "WINNER_OT_PEN",
        "WINNER_INCL_OT",
        "OVER_TOTAL",
        "OVER_UNDER",
        "HANDICAP",
        "ASIAN_HANDICAP",
        "PERIOD_1_1X2",
        "PERIOD_2_1X2",
        "PERIOD_3_1X2",
    ],
    tennis: [
        "WINNER",
        "SET_WINNER",
        "SET_1_WINNER",
        "SET_2_WINNER",
        "SET_3_WINNER",
        "CORRECT_SCORE",
        "TOTAL_GAMES",
        "OVER_GAMES",
        "OVER_UNDER_GAMES",
        "TOTAL_SETS",
    ],
};

export function allMarketsForSport(sport: LivescoreSport): string[] {
    return SPORT_ODDS_MARKETS[sport] ?? SPORT_ODDS_MARKETS.football;
}
